/**
 * Ahrefs Unit Budget Tracking + Circuit-Breaker (Story 20.1, AC #5)
 *
 * Ahrefs charges per row × field, NOT per request — a sloppy widget config
 * can burn an Enterprise customer's monthly quota in minutes. We track
 * actual unit consumption (not request count) in KV with a 60-day TTL, and
 * enforce a per-tenant daily circuit-breaker that pauses sync calls when
 * the daily budget is exceeded.
 *
 * Why per-tenant, not per-integration: a multi-client agency may have many
 * Ahrefs integrations sharing the same workspace quota. Capping per-tenant
 * (rather than per-integration) prevents one runaway client from starving
 * the others. The default 5,000,000 units/day cap is ~30× the steady-state
 * burn for a 5-client agency at 4-hour cache TTL.
 *
 * Carry-forward from Snapchat 19.7 + Reddit 19.10 — same pattern of
 * circuit-breaker-then-notify-once-per-occurrence.
 */

import type { KVStore } from "../cache";

// ============================================================================
// Constants (AC #5)
// ============================================================================

/**
 * Per-tenant daily unit budget. Calls are rejected with `rate_limited` once
 * the day's accumulated consumption crosses this threshold.
 *
 * Sized at ~30× the steady-state burn for a 5-client agency at 4-hour cache
 * TTL (5 clients × 4 endpoint groups × 50 units × 6 cache-misses/day ≈ 6000
 * units; 5,000,000 leaves an 800× headroom for one-off heavy widgets like
 * batch-analysis on big domains). Easy to tune via env override later.
 */
export const AHREFS_DAILY_TENANT_UNIT_BUDGET = 5_000_000;

/**
 * KV TTL for daily roll-up keys. 60 days lets us answer "units used last
 * month" for the integration card UI without separate aggregate keys.
 */
export const AHREFS_UNITS_KV_TTL_SEC = 60 * 24 * 3600;

// ============================================================================
// Key construction
// ============================================================================

/**
 * Per-integration daily key. Used by the integration card UI's
 * `GET /:integrationId/ahrefs/usage` endpoint.
 */
export function buildAhrefsIntegrationUnitsKey(
  integrationId: string,
  yyyyMmDd: string
): string {
  return `ahrefs:units_used:integration:${integrationId}:${yyyyMmDd}`;
}

/**
 * Per-tenant daily key. Used by the circuit-breaker.
 */
export function buildAhrefsTenantUnitsKey(
  tenantId: string,
  yyyyMmDd: string
): string {
  return `ahrefs:units_used:tenant:${tenantId}:${yyyyMmDd}`;
}

/**
 * Marker key — set once when the tenant first crosses the daily budget.
 * Prevents the notification system from spamming once per call instead of
 * once per breach. TTL aligns with the daily reset at UTC midnight.
 */
export function buildAhrefsTenantBreachMarkerKey(
  tenantId: string,
  yyyyMmDd: string
): string {
  return `ahrefs:budget_breach:${tenantId}:${yyyyMmDd}`;
}

/** Today's date in `YYYY-MM-DD` UTC form. */
export function todayUtcYyyyMmDd(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

// ============================================================================
// KV roll-up helpers
// ============================================================================

/**
 * Increment the per-integration AND per-tenant daily counters by `units`.
 * Both counters share the 60-day TTL so the integration card UI can show
 * "units used today" and "units used this month" without separate jobs.
 *
 * NOTE: KV does not support atomic increment. Under concurrent calls, two
 * workers can read the same value, increment, and write back — losing one
 * increment. This is the same trade-off PageSpeed accepts for its rate
 * limit counters. The unit budget is a soft safety net (Ahrefs enforces
 * the hard cap server-side), so the drift is acceptable.
 *
 * @returns the post-increment per-tenant total (for circuit-breaker checks).
 */
export async function incrementAhrefsUnitsUsed(
  kv: KVStore,
  tenantId: string,
  integrationId: string,
  units: number,
  date: string = todayUtcYyyyMmDd()
): Promise<number> {
  if (!Number.isFinite(units) || units < 0) {
    return 0;
  }
  const tenantKey = buildAhrefsTenantUnitsKey(tenantId, date);
  const integrationKey = buildAhrefsIntegrationUnitsKey(integrationId, date);

  const [tenantRaw, integrationRaw] = await Promise.all([
    kv.get(tenantKey),
    kv.get(integrationKey),
  ]);

  const tenantTotal = (Number(tenantRaw ?? "0") || 0) + units;
  const integrationTotal = (Number(integrationRaw ?? "0") || 0) + units;

  await Promise.all([
    kv.put(tenantKey, String(tenantTotal), { expirationTtl: AHREFS_UNITS_KV_TTL_SEC }),
    kv.put(integrationKey, String(integrationTotal), {
      expirationTtl: AHREFS_UNITS_KV_TTL_SEC,
    }),
  ]);

  return tenantTotal;
}

/**
 * Read the per-integration daily total. Used by the usage UI.
 */
export async function getAhrefsIntegrationDailyUnits(
  kv: KVStore,
  integrationId: string,
  date: string = todayUtcYyyyMmDd()
): Promise<number> {
  const raw = await kv.get(buildAhrefsIntegrationUnitsKey(integrationId, date));
  return Number(raw ?? "0") || 0;
}

/**
 * Read the per-tenant daily total. Used by the circuit-breaker.
 */
export async function getAhrefsTenantDailyUnits(
  kv: KVStore,
  tenantId: string,
  date: string = todayUtcYyyyMmDd()
): Promise<number> {
  const raw = await kv.get(buildAhrefsTenantUnitsKey(tenantId, date));
  return Number(raw ?? "0") || 0;
}

// ============================================================================
// Circuit-breaker
// ============================================================================

export interface AhrefsBudgetCheckResult {
  /** True iff the tenant is over the daily cap. Adapter rejects further calls. */
  readonly exceeded: boolean;
  /** Current accumulated units for the day. */
  readonly current: number;
  /** Configured daily limit (`AHREFS_DAILY_TENANT_UNIT_BUDGET`). */
  readonly limit: number;
  /** True iff THIS check is the first to cross the threshold today. */
  readonly firstBreach: boolean;
}

/**
 * Check whether the tenant's current daily unit consumption has exceeded the
 * configured cap. When the cap is crossed for the first time today, a
 * marker key is set so subsequent calls return `firstBreach: false` (caller
 * uses this to decide whether to fire the per-occurrence notification).
 *
 * The marker key is cleared by its TTL at UTC midnight, naturally resetting
 * the breach state for the next day.
 */
export async function checkAhrefsUnitBudget(
  kv: KVStore,
  tenantId: string,
  limit: number = AHREFS_DAILY_TENANT_UNIT_BUDGET,
  date: string = todayUtcYyyyMmDd()
): Promise<AhrefsBudgetCheckResult> {
  const current = await getAhrefsTenantDailyUnits(kv, tenantId, date);
  const exceeded = current >= limit;
  if (!exceeded) {
    return { exceeded: false, current, limit, firstBreach: false };
  }

  const markerKey = buildAhrefsTenantBreachMarkerKey(tenantId, date);
  const markerRaw = await kv.get(markerKey);
  const firstBreach = markerRaw === null;
  if (firstBreach) {
    // TTL roughly bounds to end-of-UTC-day — 24h is the safe ceiling.
    await kv.put(markerKey, String(Date.now()), { expirationTtl: 24 * 3600 });
  }
  return { exceeded: true, current, limit, firstBreach };
}
